function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import axios from 'axios';
import * as d3 from 'd3-ease';
import 'mapbox-gl/dist/mapbox-gl.css';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import ReactMapGL, { FlyToInterpolator, Marker, TRANSITION_EVENTS } from 'react-map-gl';
import MarkerSvg from '../../static/marker-orange.svg';
import { franceViewport, parisViewport } from '../../utils/map';
import { buildMapboxURI } from '../../utils/mapbox';
import { layers as baseLayers, sources as baseSources } from './layers/background';
import { highlightsLayers, sources as cadasterSources } from './layers/cadaster';
import { MapHomeBlock, ThrottleButton } from './styled';
const MapContainer = _ref => {
  let {
    project
  } = _ref;
  const token = window.processRuntime.env?.MAPBOX_ACCESS_TOKEN;

  // In case the transitionDuration is changed, think about update the setTimeout duration on the useeffect (should be transitionDuration +1000ms)
  const initialViewportZoomOptions = {
    transitionDuration: 3000,
    transitionInterruption: TRANSITION_EVENTS.UPDATE,
    transitionInterpolator: new FlyToInterpolator({
      speed: 1.2
    }),
    transitionDuration: 'auto',
    transitionEasing: d3.easeCubic
  };
  const initialViewport = {
    ...franceViewport
  };
  const [state, setState] = useState({
    viewport: initialViewport,
    throttled: window.processRuntime.env?.NODE_ENV === 'development' || false,
    showMarker: false,
    mapStyle: {
      version: 8,
      metadata: {
        'mapbox:autocomposite': true,
        'mapbox:type': 'template'
      },
      sources: {
        'mapbox-raster': baseSources['mapbox-raster'],
        'ign-raster': baseSources['mapbox-raster'],
        // 'ign-raster': baseSources['ign-raster'],
        ...cadasterSources
      },
      layers: [...baseLayers, ...Object.values(highlightsLayers(project.plotsIds))],
      glyphs: buildMapboxURI('/fonts/v1/permettez-moi-de-construire/{fontstack}/{range}.pbf').toString()
    }
  });
  const onViewportChange = nextViewport => {
    if (isMountedRef.current) {
      setState(prevState => ({
        ...prevState,
        viewport: nextViewport
      }));
    }
  };
  const getCoordinatesFromAddress = async address => {
    try {
      // Utilisation de l'API BAN (Base Adresse Nationale) au lieu de Google
      const {
        data
      } = await axios.get(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`);
      if (data?.features?.length) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        return {
          lat: latitude,
          lng: longitude
        };
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  };
  const setViewportToParis = () => {
    setState(prevState => ({
      ...prevState,
      viewport: {
        ...prevState.viewport,
        ...parisViewport,
        ...initialViewportZoomOptions,
        zoom: 15
      }
    }));
  };
  const handleUnthrottle = () => {
    setState(prevState => ({
      ...prevState,
      throttled: false
    }));
  };
  const isMountedRef = useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    if (!state.throttled) {
      if (project.location && project.location.coordinates) {
        setState(prevState => ({
          ...prevState,
          viewport: {
            ...prevState.viewport,
            longitude: project.location.coordinates[0],
            latitude: project.location.coordinates[1],
            zoom: 16.5,
            ...initialViewportZoomOptions
          }
        }));
      } else if (project.address) {
        getCoordinatesFromAddress(project.address).then(data => {
          if (isMountedRef.current) {
            if (data) {
              setState(prevState => ({
                ...prevState,
                viewport: {
                  ...prevState.viewport,
                  longitude: data.lng,
                  latitude: data.lat,
                  zoom: 16.5,
                  ...initialViewportZoomOptions
                }
              }));
            } else {
              setViewportToParis();
            }
          }
        }).catch(() => {
          if (isMountedRef.current) setViewportToParis();
        });
      } else {
        if (isMountedRef.current) setViewportToParis();
      }
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [state.throttled, project.location, project.address]);
  React.useEffect(() => {
    isMountedRef.current = true;
    let timeout;
    if (!state.throttled) {
      timeout = setTimeout(() => {
        if (isMountedRef.current) setState(prevState => ({
          ...prevState,
          showMarker: true
        }));
      }, 4000);
    }
    return () => {
      isMountedRef.current = false;
      if (timeout) clearTimeout(timeout);
    };
  }, [state.viewport.zoom]);
  return /*#__PURE__*/React.createElement(MapHomeBlock, null, state.throttled === false ? /*#__PURE__*/React.createElement(ReactMapGL, _extends({}, state.viewport, {
    width: "100%",
    height: "100%",
    onViewportChange: nextViewport => onViewportChange(nextViewport),
    mapboxApiAccessToken: token,
    mapStyle: state.mapStyle,
    scrollZoom: false,
    dragPan: false,
    dragRotate: false,
    doubleClickZoom: false,
    touchZoom: false,
    touchRotate: false,
    attributionControl: false,
    touchAction: "pan-x"
  }), state.showMarker && /*#__PURE__*/React.createElement(Marker, {
    latitude: state.viewport.latitude,
    longitude: state.viewport.longitude,
    offsetLeft: -5,
    offsetTop: -30
  }, /*#__PURE__*/React.createElement("img", {
    src: MarkerSvg
  }))) : /*#__PURE__*/React.createElement(ThrottleButton, {
    onClick: handleUnthrottle
  }, "Charger la carte"));
};
MapContainer.propTypes = {
  project: PropTypes.object.isRequired
};
export default MapContainer;